import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Calendar, BookMarked, Share2, Facebook, Twitter, Linkedin, Download, ArrowLeft, Mail, BookOpen, FileText, ChevronDown, ChevronUp, Clock, Users, MapPin, ChevronRight, Copy, Check, Printer } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { publications, categoryConfig } from '../../data/publications';
import type { Publication } from '../../types/publications';
import { getPublicationPdfUrl, hasValidPdfUrl } from '../../utils/storage';
import { supabase } from '../../lib/supabase';

// Google Form field IDs - these match the actual form fields
const GOOGLE_FORM = {
  ID: '1eLsgAa7nXlwSXHY4N8klZsFdRk2b5o4CQFQYSoFOS_Y',
  FIELDS: {
    EMAIL: 'entry.1045781291',
    PUBLICATION_TITLE: 'entry.1234567890',
    RESEARCH_INTERESTS: 'entry.1234567891',
    PROFESSIONAL_BACKGROUND: 'entry.1234567892',
    HEARD_ABOUT_US: 'entry.1234567893'
  }
} as const;

interface FormData {
  email: string;
  researchInterests: string[];
  professionalBackground: string;
  heardAboutUs: string;
}

const INITIAL_FORM_DATA: FormData = {
  email: '',
  researchInterests: [],
  professionalBackground: '',
  heardAboutUs: ''
};

const RESEARCH_INTERESTS = [
  'Foundational Histories',
  'Governance and Leadership',
  'Culture and Heritage',
  'Urbanization and Development',
  'Conflicts and Wars'
] as const;

const PROFESSIONAL_BACKGROUNDS = [
  'Student',
  'Academic/Researcher',
  'Historian',
  'Cultural Enthusiast',
  'Other'
] as const;

const HEARD_ABOUT_US_OPTIONS = [
  'Academic Reference',
  'Social Media',
  'Search Engine',
  'Word of Mouth',
  'Other'
] as const;

const PublicationDetailSkeleton = () => (
  <div className="min-h-screen bg-[#F5F5DC] pt-20">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 animate-pulse">
      <div className="h-6 w-48 bg-slate-300 rounded mb-6" />

      <div className="mb-8">
        <div className="w-full h-48 sm:h-64 bg-slate-300 rounded-xl mb-6" />
        <div className="h-8 bg-slate-300 rounded w-3/4 mb-4" />
        <div className="flex flex-wrap gap-4 mb-4">
          <div className="h-6 w-32 bg-slate-300 rounded" />
          <div className="h-6 w-40 bg-slate-300 rounded" />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
        <div className="h-6 w-32 bg-slate-300 rounded mb-4" />
        <div className="space-y-2">
          <div className="h-4 bg-slate-300 rounded w-full" />
          <div className="h-4 bg-slate-300 rounded w-5/6" />
          <div className="h-4 bg-slate-300 rounded w-4/5" />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="space-y-4">
          <div className="h-4 bg-slate-300 rounded w-full" />
          <div className="h-4 bg-slate-300 rounded w-11/12" />
          <div className="h-4 bg-slate-300 rounded w-full" />
          <div className="h-4 bg-slate-300 rounded w-4/5" />
        </div>
      </div>
    </div>
  </div>
);

export default function PublicationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tocExpanded, setTocExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [copiedCitation, setCopiedCitation] = useState<string | null>(null);

  // Add useEffect to scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [id]); // Dependency on id ensures scroll happens when publication changes

  const publication = publications.find(p => p.id === Number(id));

  // Check if publication has full content
  const hasFullContent = publication &&
    publication.content &&
    publication.content.trim().length > 0 &&
    publication.abstract !== "Publication forthcoming.";

  // Get related publications
  const getRelatedPublications = (currentPub: Publication): Publication[] => {
    // First, get publications from the same category
    const sameCategory = publications.filter(
      p => p.id !== currentPub.id && p.category === currentPub.category
    );

    // If we have 3 or more from same category, return first 3
    if (sameCategory.length >= 3) {
      return sameCategory.slice(0, 3);
    }

    // Otherwise, fill with publications from other categories
    const otherCategories = publications.filter(
      p => p.id !== currentPub.id && p.category !== currentPub.category
    );

    return [...sameCategory, ...otherCategories].slice(0, 3);
  };

  const relatedPublications = publication ? getRelatedPublications(publication) : [];

  if (isLoading) {
    return <PublicationDetailSkeleton />;
  }

  if (!publication) {
    return (
      <div className="min-h-screen bg-[#F5F5DC] pt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-4xl font-bold text-[#064635]">Publication not found</h1>
        </div>
      </div>
    );
  }

  const handleDownload = () => {
    setShowForm(true);
  };

  const handleCopyCitation = async (format: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCitation(format);
      setTimeout(() => {
        setCopiedCitation(null);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy citation:', error);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      // Save to Supabase subscribers table
      try {
        const { error: saveError } = await supabase
          .from('subscribers')
          .insert({
            email: formData.email,
            publication_id: publication.id,
            research_interests: formData.researchInterests,
            professional_background: formData.professionalBackground,
            how_heard_about_us: formData.heardAboutUs
          });

        if (saveError) {
          console.error('Error saving to Supabase:', saveError);
          // Continue with download even if save fails
        }
      } catch (saveError) {
        console.error('Error saving subscriber data:', saveError);
        // Don't block download if save fails
      }

      // Show brief confirmation
      setFormData(INITIAL_FORM_DATA);
      setShowForm(false);

      // Brief delay for "Thank you" effect
      await new Promise(resolve => setTimeout(resolve, 500));

      // Trigger download using Supabase Storage URL
      const pdfUrl = getPublicationPdfUrl(publication.pdfUrl);
      if (pdfUrl) {
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = `${publication.title}.pdf`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5DC] pt-24 print:pt-0 print:bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 print:px-0 print:py-0 print:max-w-none">
        {/* Breadcrumbs */}
        <nav className="flex items-center text-sm mb-6 overflow-x-auto print:hidden">
          <Link to="/" className="text-[#064635] hover:text-[#D4AF37] transition-colors whitespace-nowrap">
            Home
          </Link>
          <ChevronRight className="h-4 w-4 mx-2 text-gray-400 flex-shrink-0" />
          <Link to="/publications" className="text-[#064635] hover:text-[#D4AF37] transition-colors whitespace-nowrap">
            Publications
          </Link>
          <ChevronRight className="h-4 w-4 mx-2 text-gray-400 flex-shrink-0" />
          <Link
            to={`/publications/category/${publication.category}`}
            className="text-[#064635] hover:text-[#D4AF37] transition-colors whitespace-nowrap"
          >
            {categoryConfig[publication.category]?.title || publication.category}
          </Link>
          <ChevronRight className="h-4 w-4 mx-2 text-gray-400 flex-shrink-0" />
          <span className="text-gray-600 truncate">{publication.title}</span>
        </nav>

        {/* Header */}
        <div className="publication-header mb-6 print:mb-2">
          <img
            src={publication.coverImage}
            alt={publication.title}
            className="w-full h-48 sm:h-64 object-cover rounded-xl shadow-lg mb-4 print:hidden"
          />
          <h1 className="text-2xl sm:text-3xl font-bold text-[#064635] mb-2 break-words print:text-4xl print:mb-2">{publication.title}</h1>
          {publication.pageCount && (
            <div className="flex items-center text-gray-600 mb-3 print:text-black print:mb-1">
              <FileText className="h-5 w-5 mr-2 print:hidden" />
              <span className="print:font-semibold">{publication.pageCount} pages</span>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-sm sm:text-base text-gray-600 print:text-black print:mb-2">
            <div className="flex items-center">
              <Calendar className="h-5 w-5 mr-2 print:hidden" />
              <span><span className="hidden print:inline font-semibold">Date: </span>{publication.date}</span>
            </div>
            <div className="flex items-center">
              <BookMarked className="h-5 w-5 mr-2 print:hidden" />
              <span className="capitalize"><span className="hidden print:inline font-semibold">Category: </span>{publication.category.replace('-', ' ')}</span>
            </div>
            {publication.historicalPeriod && (
              <div className="flex items-center">
                <Clock className="h-5 w-5 mr-2 print:hidden" />
                <span className="px-3 py-1 bg-[#064635] text-white rounded-full text-sm font-medium print:bg-transparent print:text-black print:px-0 print:py-0">
                  <span className="hidden print:inline font-semibold">Period: </span>{publication.historicalPeriod}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Keywords */}
        {publication.keywords && publication.keywords.length > 0 && (
          <div className="mb-4 print:hidden">
            <div className="flex flex-wrap gap-2">
              {publication.keywords.map((keyword, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-[#D4AF37]/20 text-[#064635] rounded-full text-sm font-medium"
                >
                  {keyword}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Key Historical Figures */}
        {publication.keyFigures && publication.keyFigures.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-4 print:hidden">
            <div className="flex items-center mb-3">
              <Users className="h-5 w-5 text-[#D4AF37] mr-2" />
              <h3 className="text-lg font-semibold text-[#064635]">Key Historical Figures</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {publication.keyFigures.map((figure, index) => (
                <span
                  key={index}
                  className="px-3 py-1.5 bg-[#064635]/5 text-[#064635] rounded-lg text-sm font-medium border border-[#064635]/10"
                >
                  {figure}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Key Locations */}
        {publication.keyLocations && publication.keyLocations.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-4 print:hidden">
            <div className="flex items-center mb-3">
              <MapPin className="h-5 w-5 text-[#D4AF37] mr-2" />
              <h3 className="text-lg font-semibold text-[#064635]">Key Locations</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {publication.keyLocations.map((location, index) => (
                <span
                  key={index}
                  className="px-3 py-1.5 bg-[#064635]/5 text-[#064635] rounded-lg text-sm font-medium border border-[#064635]/10"
                >
                  {location}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Sample Excerpt */}
        {publication.sampleExcerpt && (
          <div className="bg-white rounded-lg shadow-lg p-8 mb-4 print:hidden">
            <p className="text-sm italic text-[#D4AF37] mb-4">From the Introduction</p>
            <blockquote className="border-l-4 border-[#D4AF37] pl-6 italic text-gray-700 text-lg leading-relaxed">
              {publication.sampleExcerpt}
            </blockquote>
          </div>
        )}

        {/* Table of Contents */}
        {publication.tableOfContents && publication.tableOfContents.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-4 print:shadow-none print:border print:border-gray-300 print:p-4 print:mb-4">
            <button
              onClick={() => setTocExpanded(!tocExpanded)}
              className="flex items-center justify-between w-full text-left print:pointer-events-none"
            >
              <h3 className="text-lg font-semibold text-[#064635] print:text-xl">Table of Contents</h3>
              <span className="print:hidden">
                {tocExpanded ? (
                  <ChevronUp className="h-5 w-5 text-[#064635]" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-[#064635]" />
                )}
              </span>
            </button>
            <div className={`mt-4 space-y-2 ${tocExpanded ? 'block' : 'hidden'} print:block print:mt-2`}>
              {publication.tableOfContents.map((item, index) => (
                <div
                  key={index}
                  className="flex justify-between items-start py-2 border-b border-gray-100 last:border-0"
                >
                  <span className="text-gray-700">{item.chapter}</span>
                  {item.page && (
                    <span className="text-gray-500 text-sm ml-4 flex-shrink-0">
                      p. {item.page}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {hasFullContent ? (
          <>
            {/* Abstract and Content - Continuous Prose */}
            <div className="bg-white rounded-lg shadow-lg p-8 mb-8 prose prose-lg max-w-none print:shadow-none print:border-0 print:rounded-none print:p-0">
              <h2 className="text-2xl font-bold text-[#064635] mb-4">Abstract</h2>
              <p className="text-gray-700 mb-8 leading-relaxed">{publication.abstract}</p>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{publication.content}</ReactMarkdown>
            </div>

            {/* Download CTA */}
            {hasValidPdfUrl(publication.pdfUrl) && (
              <div className="bg-[#064635] text-white rounded-lg shadow-lg p-8 mb-12 print:hidden">
                <h3 className="text-2xl font-bold mb-4">Want to read more?</h3>
                <p className="mb-6">
                  Join our community of researchers and historians. Get access to the full publication and stay updated with our latest research and discoveries.
                </p>
                <button
                  onClick={handleDownload}
                  className="bg-[#D4AF37] text-[#064635] px-6 py-3 rounded-lg font-semibold
                           hover:bg-opacity-90 transition flex items-center justify-center"
                >
                  <Download className="h-5 w-5 mr-2" />
                  Download Full Publication
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Coming Soon Message */}
            <div className="bg-white rounded-lg shadow-lg p-8 mb-8 print:hidden">
              <div className="flex items-start space-x-4">
                <BookOpen className="h-12 w-12 text-[#D4AF37] flex-shrink-0" />
                <div>
                  <h2 className="text-2xl font-semibold text-[#064635] mb-4">
                    Full Digital Version Coming Soon
                  </h2>
                  <p className="text-gray-700 mb-4">
                    This publication is part of the Echoes of Ibadan archive. We are currently digitizing
                    our extensive collection of {publications.length}+ publications to make them accessible online.
                  </p>
                  <p className="text-gray-700">
                    Category: <span className="font-semibold text-[#064635]">
                      {categoryConfig[publication.category]?.title || publication.category}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* Show EITHER download OR request - never both */}
            {hasValidPdfUrl(publication.pdfUrl) ? (
              <div className="bg-[#064635] text-white rounded-lg shadow-lg p-8 mb-8 print:hidden">
                <h3 className="text-2xl font-bold mb-4">Access the Full Publication</h3>
                <p className="mb-6">
                  While the digital version is being prepared, you can download the PDF version to explore
                  this comprehensive research on {publication.title.toLowerCase()}.
                </p>
                <button
                  onClick={handleDownload}
                  className="bg-[#D4AF37] text-[#064635] px-6 py-3 rounded-lg font-semibold
                           hover:bg-opacity-90 transition flex items-center justify-center"
                >
                  <Download className="h-5 w-5 mr-2" />
                  Download Full Publication
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-lg p-8 mb-8 print:hidden">
                <h3 className="text-xl font-semibold text-[#064635] mb-4">Request This Publication</h3>
                <p className="text-gray-700 mb-6">
                  Interested in this research? Contact us to learn more about accessing this publication
                  or to inquire about related materials in our archive.
                </p>
                <Link
                  to={`/contact?subject=${encodeURIComponent(`Request: ${publication.title}`)}`}
                  className="inline-flex items-center bg-[#D4AF37] text-[#064635] px-6 py-3 rounded-lg font-semibold
                         hover:bg-opacity-90 transition"
                >
                  <Mail className="h-5 w-5 mr-2" />
                  Contact Us
                </Link>
              </div>
            )}
          </>
        )}

        {/* Download Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 print:hidden">
            <div className="bg-white rounded-lg p-6 sm:p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-2xl font-bold text-[#064635] mb-6">Download Publication</h3>
              <p className="text-gray-600 mb-6">
                Enter your details to access the full publication. We'll also keep you updated with new research and publications.
              </p>
              <form onSubmit={handleFormSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Research Interests
                  </label>
                  <div className="space-y-2">
                    {RESEARCH_INTERESTS.map((interest) => (
                      <label key={interest} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.researchInterests.includes(interest)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData(prev => ({
                                ...prev,
                                researchInterests: [...prev.researchInterests, interest]
                              }));
                            } else {
                              setFormData(prev => ({
                                ...prev,
                                researchInterests: prev.researchInterests.filter(i => i !== interest)
                              }));
                            }
                          }}
                          className="rounded border-gray-300 text-[#D4AF37] focus:ring-[#D4AF37]"
                        />
                        <span className="ml-2 text-gray-700">{interest}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="professionalBackground" className="block text-sm font-medium text-gray-700 mb-1">
                    Professional Background *
                  </label>
                  <select
                    id="professionalBackground"
                    required
                    value={formData.professionalBackground}
                    onChange={(e) => setFormData(prev => ({ ...prev, professionalBackground: e.target.value }))}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                  >
                    <option value="">Select an option</option>
                    {PROFESSIONAL_BACKGROUNDS.map((background) => (
                      <option key={background} value={background}>{background}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="heardAboutUs" className="block text-sm font-medium text-gray-700 mb-1">
                    How did you hear about us?
                  </label>
                  <select
                    id="heardAboutUs"
                    value={formData.heardAboutUs}
                    onChange={(e) => setFormData(prev => ({ ...prev, heardAboutUs: e.target.value }))}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                  >
                    <option value="">Select an option</option>
                    {HEARD_ABOUT_US_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div className="flex justify-end space-x-4 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-[#064635] text-white px-6 py-2 rounded-lg font-semibold 
                             hover:bg-opacity-90 transition flex items-center disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Download className="h-5 w-5 mr-2" />
                        Download
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Citations */}
        {publication.citations && (
          <div className="bg-white rounded-lg shadow-lg p-8 mb-12 print:shadow-none print:border print:border-gray-300 print:p-4 print:mb-4">
            <h2 className="text-2xl font-semibold text-[#064635] mb-6 print:mb-3">How to Cite</h2>
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-[#064635]">APA</h3>
                  <button
                    onClick={() => handleCopyCitation('apa', publication.citations!.apa)}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-[#064635] hover:text-[#D4AF37]
                             border border-[#064635] hover:border-[#D4AF37] rounded-lg transition print:hidden"
                  >
                    {copiedCitation === 'apa' ? (
                      <>
                        <Check className="h-4 w-4" />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed print:text-black">{publication.citations.apa}</p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-[#064635]">MLA</h3>
                  <button
                    onClick={() => handleCopyCitation('mla', publication.citations!.mla)}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-[#064635] hover:text-[#D4AF37]
                             border border-[#064635] hover:border-[#D4AF37] rounded-lg transition print:hidden"
                  >
                    {copiedCitation === 'mla' ? (
                      <>
                        <Check className="h-4 w-4" />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed print:text-black">{publication.citations.mla}</p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-[#064635]">Chicago</h3>
                  <button
                    onClick={() => handleCopyCitation('chicago', publication.citations!.chicago)}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-[#064635] hover:text-[#D4AF37]
                             border border-[#064635] hover:border-[#D4AF37] rounded-lg transition print:hidden"
                  >
                    {copiedCitation === 'chicago' ? (
                      <>
                        <Check className="h-4 w-4" />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed print:text-black">{publication.citations.chicago}</p>
              </div>
            </div>
          </div>
        )}

        {/* Related Publications */}
        {relatedPublications.length > 0 && (
          <div className="mb-12 print:hidden">
            <h2 className="text-2xl font-semibold text-[#064635] mb-6">Related Publications</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedPublications.map((relatedPub) => (
                <Link
                  key={relatedPub.id}
                  to={`/publications/${relatedPub.id}`}
                  className="group bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                >
                  <img
                    src={relatedPub.coverImage}
                    alt={relatedPub.title}
                    className="w-full h-40 object-cover"
                  />
                  <div className="p-4">
                    <div className="text-xs text-[#D4AF37] font-medium mb-2 uppercase">
                      {categoryConfig[relatedPub.category]?.title || relatedPub.category}
                    </div>
                    <h3 className="font-semibold text-[#064635] group-hover:text-[#D4AF37] transition-colors line-clamp-2">
                      {relatedPub.title}
                    </h3>
                    <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                      {relatedPub.abstract}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Share and Print */}
        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 print:hidden">
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-[#064635] text-white rounded-lg
                     hover:bg-opacity-90 transition font-medium"
          >
            <Printer className="h-5 w-5" />
            Print This Page
          </button>
          <div className="flex items-center space-x-4">
            <span className="text-[#064635] font-medium">Share:</span>
            <button className="p-2 rounded-full hover:bg-white/50 transition">
              <Facebook className="h-5 w-5 text-[#064635]" />
            </button>
            <button className="p-2 rounded-full hover:bg-white/50 transition">
              <Twitter className="h-5 w-5 text-[#064635]" />
            </button>
            <button className="p-2 rounded-full hover:bg-white/50 transition">
              <Linkedin className="h-5 w-5 text-[#064635]" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}